import {
  forwardRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '../../lib/cn'

/* ----------------------------- Botón -----------------------------
   Alto mínimo 48px en todas las variantes: la app se usa de pie, con
   una mano, a veces con guantes de cámara de frío. */

type Variante = 'primario' | 'secundario' | 'fantasma' | 'peligro' | 'exito'
type Tamano = 'sm' | 'md' | 'lg'

const VARIANTES: Record<Variante, string> = {
  primario:
    'bg-bosque-500 text-white hover:bg-bosque-600 active:bg-bosque-700 shadow-sm shadow-bosque-500/20',
  secundario:
    'superficie-elevada texto-base border borde-base hover:superficie-hundida active:scale-[0.98]',
  fantasma: 'texto-suave hover:superficie-hundida hover:texto-base',
  peligro: 'bg-rojo-500 text-white hover:bg-rojo-600 active:bg-rojo-700',
  exito: 'bg-lima-500 text-white hover:bg-lima-600 active:bg-lima-700',
}

const TAMANOS: Record<Tamano, string> = {
  sm: 'h-10 px-3.5 text-sm gap-1.5 rounded-xl',
  md: 'h-12 px-5 text-[15px] gap-2 rounded-2xl',
  lg: 'h-14 px-6 text-base gap-2.5 rounded-2xl',
}

interface BotonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: Variante
  tamano?: Tamano
  cargando?: boolean
  bloque?: boolean
  icono?: ReactNode
}

export const Boton = forwardRef<HTMLButtonElement, BotonProps>(function Boton(
  { variante = 'primario', tamano = 'md', cargando, bloque, icono, className, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || cargando}
      className={cn(
        'inline-flex items-center justify-center font-semibold whitespace-nowrap',
        'transition-all duration-200 ease-[var(--ease-suave)]',
        'disabled:opacity-40 disabled:pointer-events-none select-none',
        VARIANTES[variante],
        TAMANOS[tamano],
        bloque && 'w-full',
        className,
      )}
      {...props}
    >
      {cargando ? <Loader2 className="size-[1.15em] animate-spin" /> : icono}
      {children}
    </button>
  )
})

/** Botón solo-ícono, cuadrado, para barras de navegación. */
export const BotonIcono = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { etiqueta: string; activo?: boolean }
>(function BotonIcono({ etiqueta, activo, className, children, ...props }, ref) {
  return (
    <button
      ref={ref}
      aria-label={etiqueta}
      title={etiqueta}
      className={cn(
        'grid size-11 shrink-0 place-items-center rounded-xl transition-colors duration-200',
        activo ? 'bg-bosque-50 text-bosque-600 dark:bg-bosque-500/20 dark:text-bosque-200' : 'texto-suave hover:superficie-hundida hover:texto-base',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
})

/* ----------------------------- Tarjeta ----------------------------- */

export function Tarjeta({
  className,
  children,
  onClick,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const clicable = Boolean(onClick)
  return (
    <div
      onClick={onClick}
      role={clicable ? 'button' : undefined}
      tabIndex={clicable ? 0 : undefined}
      onKeyDown={
        clicable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                ;(e.currentTarget as HTMLElement).click()
              }
            }
          : undefined
      }
      className={cn(
        'superficie-elevada rounded-3xl border borde-suave sombra-carta',
        clicable &&
          'cursor-pointer transition-all duration-200 ease-[var(--ease-suave)] hover:sombra-flotante hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99]',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

/* ---------------------------- Formularios ---------------------------- */

export function Campo({
  etiqueta,
  ayuda,
  error,
  requerido,
  children,
  className,
  oscura,
}: {
  etiqueta?: string
  ayuda?: string
  error?: string
  requerido?: boolean
  children: ReactNode
  className?: string
  /** La etiqueta normal va en gris tenue —a propósito, para no competir
   *  con el contenido del campo. Cuando el campo hace de título de su
   *  propia sección (como "Estado del pedido" en Vender), conviene que
   *  se note tanto como uno: negro y un poco más grande. */
  oscura?: boolean
}) {
  return (
    <label className={cn('block', className)}>
      {etiqueta && (
        <span
          className={cn(
            'mb-1.5 flex items-center gap-1 font-semibold',
            oscura ? 'text-[15px] texto-base' : 'text-[13px] texto-suave',
          )}
        >
          {etiqueta}
          {requerido && <span className="text-rojo-500">*</span>}
        </span>
      )}
      {children}
      {error ? (
        <span className="mt-1.5 block text-[13px] font-medium text-rojo-500">{error}</span>
      ) : ayuda ? (
        <span className="mt-1.5 block text-[13px] texto-tenue">{ayuda}</span>
      ) : null}
    </label>
  )
}

const CONTROL =
  'w-full rounded-2xl border borde-base superficie-elevada px-4 text-[15px] texto-base ' +
  'placeholder:texto-tenue transition-colors duration-200 ' +
  'focus:border-bosque-400 focus:outline-none focus:ring-4 focus:ring-bosque-500/10 ' +
  'disabled:opacity-50'

export const Entrada = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Entrada({ className, ...props }, ref) {
    return <input ref={ref} className={cn(CONTROL, 'h-12', className)} {...props} />
  },
)

export const AreaTexto = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function AreaTexto({ className, ...props }, ref) {
    return <textarea ref={ref} rows={3} className={cn(CONTROL, 'py-3 leading-relaxed', className)} {...props} />
  },
)

export const Lista = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Lista({ className, children, ...props }, ref) {
    return (
      <select
        ref={ref}
        className={cn(
          CONTROL,
          'h-12 appearance-none bg-[length:18px] bg-[right_1rem_center] bg-no-repeat pr-11',
          "bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236f7a6b' stroke-width='2.5' stroke-linecap='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")]",
          className,
        )}
        {...props}
      >
        {children}
      </select>
    )
  },
)

/* ---------------------------- Etiquetas ---------------------------- */

export function Chip({
  children,
  className,
  punto,
}: {
  children: ReactNode
  className?: string
  punto?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold whitespace-nowrap',
        'superficie-hundida texto-suave',
        className,
      )}
    >
      {punto && <span className={cn('size-1.5 rounded-full', punto)} />}
      {children}
    </span>
  )
}

/* ------------------------------ Switch ------------------------------
   Para una acción de sí/no que se ve de un vistazo: verde y a la
   derecha cuando está activo, gris y a la izquierda cuando no. */

export function Switch({
  activo,
  onChange,
  disabled,
  etiqueta,
}: {
  activo: boolean
  onChange: (valor: boolean) => void
  disabled?: boolean
  /** Para lectores de pantalla —el switch no lleva texto propio. */
  etiqueta: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={activo}
      aria-label={etiqueta}
      disabled={disabled}
      onClick={() => onChange(!activo)}
      className={cn(
        'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200',
        'disabled:opacity-40 disabled:pointer-events-none',
        activo ? 'bg-lima-500' : 'superficie-hundida',
      )}
    >
      <span
        className={cn(
          'inline-block size-5 rounded-full bg-white shadow-sm transition-transform duration-200',
          activo ? 'translate-x-6' : 'translate-x-1',
        )}
      />
    </button>
  )
}

/* ------------------------- Control segmentado -------------------------
   El patrón de iOS: pocas opciones excluyentes, siempre visibles. */

export function Segmentado<T extends string>({
  valor,
  opciones,
  onChange,
  className,
  variante = 'sutil',
}: {
  valor: T
  opciones: { valor: T; etiqueta: string; icono?: ReactNode }[]
  onChange: (v: T) => void
  className?: string
  /** "marcado" resalta bastante más la pestaña activa (fondo verde
   *  sólido) —para secciones donde conviene que se note de un vistazo
   *  cuál está seleccionada, como Ventas/Clientes. */
  variante?: 'sutil' | 'marcado'
}) {
  return (
    <div
      role="tablist"
      className={cn('flex gap-1 rounded-2xl superficie-hundida p-1', className)}
    >
      {opciones.map((o) => {
        const activo = o.valor === valor
        return (
          <button
            key={o.valor}
            role="tab"
            aria-selected={activo}
            onClick={() => onChange(o.valor)}
            className={cn(
              'flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2.5',
              'text-[13px] font-semibold transition-all duration-200 ease-[var(--ease-suave)]',
              activo
                ? variante === 'marcado'
                  ? 'bg-bosque-500 text-white shadow-lg shadow-bosque-500/30 dark:bg-bosque-400 dark:text-tinta-950'
                  : 'superficie-elevada texto-base sombra-carta'
                : 'texto-suave hover:texto-base',
            )}
          >
            {o.icono}
            <span className="truncate">{o.etiqueta}</span>
          </button>
        )
      })}
    </div>
  )
}

/* ----------------------------- Vacío ----------------------------- */

export function Vacio({
  icono,
  titulo,
  detalle,
  accion,
}: {
  icono: ReactNode
  titulo: string
  detalle: string
  accion?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center anim-aparecer">
      <div className="mb-4 grid size-16 place-items-center rounded-3xl superficie-hundida texto-tenue">
        {icono}
      </div>
      <h3 className="text-lg font-semibold texto-base">{titulo}</h3>
      <p className="mt-1.5 max-w-xs text-sm texto-suave">{detalle}</p>
      {accion && <div className="mt-6">{accion}</div>}
    </div>
  )
}

/* ---------------------------- Cargando ---------------------------- */

export function Cargando({ texto = 'Cargando…' }: { texto?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 texto-tenue">
      <Loader2 className="size-7 animate-spin" />
      <p className="text-sm font-medium">{texto}</p>
    </div>
  )
}

/* ----------------------------- Avatar ----------------------------- */

const TONOS = [
  'bg-bosque-100 text-bosque-700 dark:bg-bosque-500/25 dark:text-bosque-100',
  'bg-lima-100 text-lima-700 dark:bg-lima-500/25 dark:text-lima-100',
  'bg-magenta-100 text-magenta-700 dark:bg-magenta-500/25 dark:text-magenta-100',
  'bg-tinta-100 text-tinta-700 dark:bg-tinta-500/25 dark:text-tinta-100',
  'bg-rojo-100 text-rojo-700 dark:bg-rojo-500/25 dark:text-rojo-100',
]

export function Avatar({
  nombre,
  tamano = 'md',
  className,
}: {
  nombre: string
  tamano?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  const letras = nombre
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
  // Tono estable por nombre: el mismo cliente siempre se ve igual.
  const tono = TONOS[[...nombre].reduce((a, c) => a + c.charCodeAt(0), 0) % TONOS.length]
  const medidas = { sm: 'size-8 text-[11px]', md: 'size-11 text-sm', lg: 'size-14 text-base' }[tamano]
  return (
    <div
      className={cn(
        'grid shrink-0 place-items-center rounded-2xl font-bold tracking-tight',
        medidas,
        tono,
        className,
      )}
    >
      {letras || '?'}
    </div>
  )
}

/* --------------------------- Barra de progreso --------------------------- */

export function Barra({ valor, clase }: { valor: number; clase?: string }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full superficie-hundida">
      <div
        className={cn('h-full rounded-full anim-crecer transition-all duration-500', clase ?? 'bg-bosque-500')}
        style={{ width: `${Math.min(100, Math.max(2, valor))}%` }}
      />
    </div>
  )
}
